/*
  Отчётность и сравнение версий программы (снимки append-only).
*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import {
  ArrowLeft,
  Camera,
  Download,
  FileJson,
  History,
  Printer,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { useProjects } from '@/contexts/ProjectContext';
import { diffProgramState, ganttItemsChangedCount, payloadFromLive } from '@/lib/reviewDiff';
import { cn } from '@/lib/utils';
import {
  serializeSnapshotsForExport,
  verifySnapshotIntegrity,
  type ReviewSnapshot,
} from '@/store/reviewSnapshotStore';

const STATUS_RU: Record<string, string> = {
  active: 'В работе',
  pilot: 'Пилот',
  planned: 'Запланировано',
  done: 'Завершён',
};

function formatRuDate(iso: string) {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function downloadBlob(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function progressDelta(was: number | null, now: number | null): string {
  if (was === null || now === null) return '—';
  const d = now - was;
  if (d === 0) return 'без изм.';
  return d > 0 ? `+${d}%` : `${d}%`;
}

export default function ReviewPage() {
  const {
    projects,
    ganttItems,
    reviewSnapshots,
    captureReviewSnapshot,
    canEdit,
  } = useProjects();

  const [note, setNote] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [integrityOk, setIntegrityOk] = useState<boolean | null>(null);

  const sortedSnaps = useMemo(
    () => [...reviewSnapshots].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [reviewSnapshots],
  );

  const [baselineId, setBaselineId] = useState<string>('');
  const [compareWith, setCompareWith] = useState<'live' | 'snapshot'>('live');
  const [compareSnapshotId, setCompareSnapshotId] = useState<string>('');

  useEffect(() => {
    verifySnapshotIntegrity(reviewSnapshots).then((r) => setIntegrityOk(r.ok));
  }, [reviewSnapshots]);

  useEffect(() => {
    if (sortedSnaps.length && !baselineId) {
      setBaselineId(sortedSnaps[0]!.id);
    }
  }, [sortedSnaps, baselineId]);

  const baselineSnap = sortedSnaps.find((s) => s.id === baselineId);
  const compareSnap =
    compareWith === 'snapshot' ? sortedSnaps.find((s) => s.id === compareSnapshotId) : undefined;

  const currentPayload = useMemo(
    () => payloadFromLive(projects, ganttItems),
    [projects, ganttItems],
  );

  const rightPayload =
    compareWith === 'live' ? currentPayload : compareSnap?.payload ?? null;

  const compareReady = compareWith === 'live' || !!compareSnap;

  useEffect(() => {
    if (compareWith !== 'snapshot' || !sortedSnaps.length) return;
    if (compareSnapshotId) return;
    const other = sortedSnaps.find((s) => s.id !== baselineId);
    if (other) setCompareSnapshotId(other.id);
  }, [compareWith, sortedSnaps, baselineId, compareSnapshotId]);

  useEffect(() => {
    if (compareSnapshotId && compareSnapshotId === baselineId) {
      const other = sortedSnaps.find((s) => s.id !== baselineId);
      setCompareSnapshotId(other?.id ?? '');
    }
  }, [baselineId, compareSnapshotId, sortedSnaps]);

  const diffRows = useMemo(() => {
    if (!baselineSnap || !rightPayload) return [];
    return diffProgramState(baselineSnap.payload, rightPayload);
  }, [baselineSnap, rightPayload]);

  const ganttDelta =
    baselineSnap && rightPayload
      ? ganttItemsChangedCount(baselineSnap.payload.ganttItems, rightPayload.ganttItems)
      : 0;

  const handleCapture = async () => {
    setCapturing(true);
    try {
      await captureReviewSnapshot(note);
      setNote('');
      toast.success('Снимок зафиксирован', {
        description: 'Данные добавлены в журнал. Сохраните JSON или PDF для внешней копии.',
      });
    } catch (e) {
      toast.error('Не удалось сохранить снимок', { description: String(e) });
    } finally {
      setCapturing(false);
    }
  };

  const exportOne = useCallback(
    (s: ReviewSnapshot) => {
      downloadBlob(
        `diak-snapshot-${s.id}.json`,
        serializeSnapshotsForExport([s]),
        'application/json;charset=utf-8',
      );
      toast.message('JSON скачан', { description: 'Прикрепите к письму или положите в общую папку.' });
    },
    [],
  );

  const exportAll = useCallback(() => {
    if (!reviewSnapshots.length) return;
    downloadBlob(
      `diak-snapshots-all-${new Date().toISOString().slice(0, 10)}.json`,
      serializeSnapshotsForExport(reviewSnapshots),
      'application/json;charset=utf-8',
    );
    toast.message('Архив всех снимков скачан');
  }, [reviewSnapshots]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />

      <main className="min-h-screen flex-1 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)] print:ml-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 print:px-4 print:py-3">
          {/* Screen-only controls */}
          <div className="print:hidden space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <Link href="/">
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer">
                  <ArrowLeft size={15} /> Главная
                </span>
              </Link>
            </div>

            <header className="flex flex-wrap items-start gap-4 justify-between">
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5 text-[var(--nn-blue)]" />
                  Отчётность и сравнение
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Зафиксируйте состояние перед совещанием. Снимки нельзя удалить из интерфейса — только
                  дополнять. Для прозрачности перед руководством сохраняйте JSON или PDF (печать) и
                  отправляйте копию на почту или в корпоративное хранилище.
                </p>
              </div>
              {integrityOk !== null && (
                <div
                  className={cn(
                    'flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-md border',
                    integrityOk
                      ? 'border-green-200 bg-green-50 text-green-800'
                      : 'border-amber-200 bg-amber-50 text-amber-900',
                  )}
                >
                  {integrityOk ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
                  {integrityOk ? 'Цепочка снимков целая' : 'Обнаружено расхождение хешей'}
                </div>
              )}
            </header>

            {/* Capture */}
            <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-3">
                <Camera size={16} className="text-[var(--nn-blue)]" />
                Зафиксировать текущее состояние
              </h2>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={!canEdit}
                placeholder="Комментарий к отчётной дате (необязательно): например, «Статус перед СК 02.04.2026»"
                className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 min-h-[72px] mb-3 disabled:opacity-50"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={capturing || !canEdit}
                  onClick={handleCapture}
                  className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md bg-[var(--nn-blue)] text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Camera size={16} />
                  {capturing ? 'Сохранение…' : 'Зафиксировать снимок'}
                </button>
                <button
                  type="button"
                  onClick={exportAll}
                  disabled={!reviewSnapshots.length}
                  className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md border border-border hover:bg-muted disabled:opacity-40"
                >
                  <FileJson size={16} />
                  Скачать все снимки (JSON)
                </button>
              </div>
            </section>

            {/* Journal */}
            <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <h2 className="text-sm font-semibold mb-2">Журнал снимков ({reviewSnapshots.length})</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Удаление записей из приложения не предусмотрено. Резервная копия — экспорт JSON.
              </p>
              {sortedSnaps.length === 0 ? (
                <p className="text-sm text-muted-foreground">Пока нет снимков. Создайте первый перед ближайшим совещанием.</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto text-sm">
                  {sortedSnaps.map((s) => (
                    <li
                      key={s.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-1.5 border-b border-border/60 last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{formatRuDate(s.createdAt)}</div>
                        {s.authorNote ? (
                          <div className="text-xs text-muted-foreground truncate">{s.authorNote}</div>
                        ) : null}
                        <div className="text-[10px] text-muted-foreground font-mono truncate" title={s.contentHash}>
                          hash…{s.contentHash.slice(-12)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => exportOne(s)}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border border-border hover:bg-muted"
                      >
                        <Download size={12} /> JSON
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Compare controls */}
            <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <h2 className="text-sm font-semibold mb-3">Сравнение</h2>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <label className="block space-y-1">
                  <span className="text-xs text-muted-foreground">База (прошлый отчёт)</span>
                  <select
                    value={baselineId}
                    onChange={(e) => setBaselineId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-2 py-2"
                    disabled={!sortedSnaps.length}
                  >
                    {sortedSnaps.map((s) => (
                      <option key={s.id} value={s.id}>
                        {formatRuDate(s.createdAt)}
                        {s.authorNote ? ` — ${s.authorNote.slice(0, 40)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="space-y-2">
                  <span className="text-xs text-muted-foreground block">Сравнить с</span>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="cmp"
                      checked={compareWith === 'live'}
                      onChange={() => setCompareWith('live')}
                    />
                    <span>Текущее состояние в браузере</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="cmp"
                      checked={compareWith === 'snapshot'}
                      onChange={() => setCompareWith('snapshot')}
                      disabled={sortedSnaps.length < 2}
                    />
                    <span>Другим снимком</span>
                  </label>
                  {compareWith === 'snapshot' && (
                    <select
                      value={compareSnapshotId}
                      onChange={(e) => setCompareSnapshotId(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-2 py-2"
                    >
                      <option value="">Выберите снимок</option>
                      {sortedSnaps
                        .filter((s) => s.id !== baselineId)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {formatRuDate(s.createdAt)}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handlePrint}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md border border-border hover:bg-muted"
              >
                <Printer size={16} />
                Печать / PDF
              </button>
            </section>
          </div>

          {/* Printable report */}
          <div id="review-report" className="mt-8 print:mt-0">
            <div className="rounded-xl border border-border overflow-hidden print:border print:rounded-none">
              <div className="bg-muted/50 px-4 py-3 border-b border-border print:bg-transparent">
                <h2 className="text-base font-bold">Пакет для совещания</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Сформировано:{' '}
                  {new Date().toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              {!baselineSnap ? (
                <p className="p-4 text-sm text-muted-foreground">Создайте снимок, чтобы сформировать отчёт.</p>
              ) : !compareReady ? (
                <p className="p-4 text-sm text-muted-foreground">Выберите второй снимок для сравнения.</p>
              ) : (
                <>
                  <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border/60">
                    <div>
                      <strong>База:</strong> {formatRuDate(baselineSnap.createdAt)}
                      {baselineSnap.authorNote ? ` — ${baselineSnap.authorNote}` : ''}
                    </div>
                    <div>
                      <strong>Сравнение:</strong>{' '}
                      {compareWith === 'live'
                        ? 'текущее состояние (рабочая копия)'
                        : compareSnap
                          ? formatRuDate(compareSnap.createdAt)
                          : '—'}
                    </div>
                    <div>
                      <strong>Полосы дорожной карты (изменённые строки, оценка):</strong> {ganttDelta}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2 font-semibold">Проект</th>
                          <th className="px-3 py-2 font-semibold">Прогресс</th>
                          <th className="px-3 py-2 font-semibold">Статус</th>
                          <th className="px-3 py-2 font-semibold">Открытые риски</th>
                          <th className="px-3 py-2 font-semibold">Задач в плане</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffRows.map((row) => (
                          <tr key={row.projectId} className="border-b border-border/50 hover:bg-muted/20">
                            <td className="px-3 py-2">
                              <div className="font-medium">{row.title}</div>
                              {row.presence !== 'both' && (
                                <div className="text-[10px] text-amber-700">
                                  {row.presence === 'baseline_only' ? 'Только в базе' : 'Новый в сравнении'}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 tabular-nums">
                              {row.progressWas ?? '—'}% → {row.progressNow ?? '—'}%
                              <span className="text-xs text-muted-foreground ml-1">
                                ({progressDelta(row.progressWas, row.progressNow)})
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {row.statusWas ? STATUS_RU[row.statusWas] ?? row.statusWas : '—'} →{' '}
                              {row.statusNow ? STATUS_RU[row.statusNow] ?? row.statusNow : '—'}
                            </td>
                            <td className="px-3 py-2 tabular-nums">
                              {row.openRisksWas ?? '—'} → {row.openRisksNow ?? '—'}
                            </td>
                            <td className="px-3 py-2 tabular-nums">
                              {row.ganttTasksWas ?? '—'} → {row.ganttTasksNow ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <p className="text-xs text-muted-foreground mt-4 print:mt-2 print:text-[10px]">
              Рекомендация: после фиксации снимка отправьте JSON или PDF руководству и сохраните копию вне
              браузера (почта, SharePoint). Так сохраняется то, что было показано, даже при дальнейших
              правках дашборда.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
